import { useEffect, useState } from 'react'
import { ExternalLink, Play, ShieldCheck } from 'lucide-react'
import type { CastPortrait } from './castPortraits'
import { useSocialPreview } from './useTikTokPreview'

type OfficialSocialEmbedProps = {
  portrait: CastPortrait
}

export default function OfficialSocialEmbed({ portrait }: OfficialSocialEmbedProps) {
  const preview = useSocialPreview(portrait.postUrl, portrait.platform, true)
  const [imageFailed, setImageFailed] = useState(false)
  useEffect(() => setImageFailed(false), [preview.thumbnailUrl])
  const platformName = portrait.platform === 'instagram' ? 'Instagram' : 'TikTok'
  const previewReady = preview.status === 'ready' && Boolean(preview.thumbnailUrl) && !imageFailed

  return (
    <section className="official-embed" aria-label={`Official social post from ${portrait.name}`}>
      <header className="official-embed__header">
        <div>
          <span className="official-embed__platform"><ShieldCheck size={14} /> VERIFIED PUBLIC ACCOUNT</span>
          <strong>From {portrait.shortName}&apos;s {platformName}</strong>
          <small>{portrait.handle} · hosted by {platformName}</small>
        </div>
        <a href={portrait.profileUrl} target="_blank" rel="noreferrer" aria-label={`Open ${portrait.name}'s ${platformName} profile`}>
          Profile <ExternalLink size={13} />
        </a>
      </header>

      <div className="official-embed__preview-wrap">
        {(preview.status === 'idle' || preview.status === 'loading') && <div className="official-embed__loading"><i /><i /><i /><span>Fetching the live preview from {platformName}…</span></div>}
        {previewReady && preview.thumbnailUrl && (
          <a className="official-embed__preview" href={portrait.postUrl} target="_blank" rel="noreferrer" aria-label={`Open ${portrait.name}'s official ${platformName} post`}>
            <img src={preview.thumbnailUrl} alt={portrait.accessibilitySummary} referrerPolicy="no-referrer" onError={() => setImageFailed(true)} />
            <span className="official-embed__preview-shade" />
            <span className="official-embed__preview-play"><Play size={22} fill="currentColor" /></span>
            <span className="official-embed__preview-label"><i /> OFFICIAL {platformName.toUpperCase()} PREVIEW</span>
            <span className="official-embed__preview-handle">{portrait.handle}<ExternalLink size={13} /></span>
          </a>
        )}
        {(preview.status === 'error' || imageFailed) && (
          <a className={`official-embed__fallback official-embed__gate--${portrait.tone}`} href={portrait.postUrl} target="_blank" rel="noreferrer">
            <span className="official-embed__monogram">{portrait.initials}</span>
            <span><strong>Preview unavailable here</strong><small>Open the creator-controlled original on {platformName}.</small></span>
            <ExternalLink size={18} />
          </a>
        )}
      </div>

      <footer className="official-embed__footer">
        <span>{portrait.accessibilitySummary}</span>
        <a href={portrait.postUrl} target="_blank" rel="noreferrer">Open original <ExternalLink size={12} /></a>
      </footer>
    </section>
  )
}
